import React, { FunctionComponent, useEffect, useState } from 'react'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import { useFeathersClient } from '@strac/react-client-feathers'
import { Button, Divider, Transfer, TransferProps } from 'antd'
import { Modal } from 'antd'
import { formatTimestamp } from '../../utils/time'
import { useQuery } from '@tanstack/react-query'
import { arrayToMap, getAllData } from '../../utils/data'
import { Vehicle } from 'server'
import { CloseOutlined, SaveOutlined } from '@ant-design/icons'

interface IProps {
  open: boolean
  setOpen: (value: number | undefined) => void
  initSelectedKeys: any[]
  targetKeys: any[]
  setTargetKeys: (value: any[]) => void
  handleDone: () => Promise<void>
}

interface RecordType {
  key: number
  title: string
  description: string
}

const DeviceSelectionModal: FunctionComponent<any> = (props: IProps) => {
  const { open, setOpen, initSelectedKeys, targetKeys, setTargetKeys, handleDone } = props

  const match = useMatch({ strict: false })
  const { vehicles, agencies, vehicleLocations } = useLoaderData({ from: match.routeId ?? '/' })

  const client = useFeathersClient()

  const [loading, setLoading] = useState<boolean>(false)

  // TODO: Need to get selected key to parent component

  const [selectedKeys, setSelectedKeys] = useState<TransferProps['targetKeys']>([])

  useEffect(() => {
    setTargetKeys(initSelectedKeys)
  }, [initSelectedKeys, setTargetKeys])

  const onChange: TransferProps['onChange'] = (nextTargetKeys, direction, moveKeys) => {
    setTargetKeys(nextTargetKeys)
  }

  const onSelectChange: TransferProps['onSelectChange'] = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys])
  }

  /**
   * Missing Data
   */
  const missingVehicleIds = targetKeys
    ? targetKeys
        .filter((vehicleId) => !vehicles.find((vehicle: Vehicle) => vehicle.vehicle_id === vehicleId))
        .map((vehicleId) => vehicleId)
    : []

  const missingVehicleAgencyIds = vehicles.map((vehicle: Vehicle) => vehicle.agency_id)

  const missingVehicleQuery = {
    vehicle_id: {
      $in: missingVehicleIds
    }
  }
  const { data: missingVehicleData } = useQuery({
    queryKey: ['vehicle', 'find', missingVehicleQuery],
    queryFn: () => getAllData(client, 'vehicle', missingVehicleQuery),
    enabled: !!missingVehicleIds.length,
    placeholderData: []
  })
  const { data: missingVehicleLocationData } = useQuery({
    queryKey: ['vehicle-location', 'find', missingVehicleQuery],
    queryFn: () => getAllData(client, 'vehicle-location', missingVehicleQuery),
    enabled: !!missingVehicleIds.length,
    placeholderData: []
  })
  const missingVehicleAgencyQueries = {
    id: {
      $in: missingVehicleAgencyIds
    }
  }
  const { data: missingVehicleAgencyData } = useQuery({
    queryKey: ['agencies', 'find', missingVehicleAgencyQueries],
    queryFn: () => getAllData(client, 'agencies', missingVehicleAgencyQueries),
    enabled: !!missingVehicleAgencyIds.length,
    placeholderData: []
  })

  const agenciesMap = arrayToMap([...agencies, ...missingVehicleAgencyData], 'id')
  const vehicleLocationMap = arrayToMap([...vehicleLocations, ...missingVehicleLocationData], 'vehicle_id')

  const data: RecordType[] = [...vehicles, ...missingVehicleData].map((vehicle: any): RecordType => {
    const agency = agenciesMap.get(vehicle.agency_id)
    const vehicleLocation = vehicleLocationMap.get(vehicle.vehicle_id)
    return {
      key: vehicle.vehicle_id,
      title: `(${vehicle.vehicle_id}) ${vehicle.name} ${agency ? `(${agency?.name})` : ''}`,
      description: `${agency ? `${agency?.name} - ` : ''}${
        vehicleLocation ? `${formatTimestamp(vehicleLocation.update_received)} ` : ''
      }`
    }
  })

  const handleCancel = () => {
    setOpen(undefined)
  }

  return (
    <Modal
      destroyOnClose={true}
      footer={null}
      height={'80vh'}
      width={'80vw'}
      centered={true}
      open={open}
      onCancel={handleCancel}
      closable={false}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', height: '40px', marginBottom: '15px' }}>
        <Button
          onClick={handleCancel}
          type={'primary'}
          size={'large'}
          danger={true}
          icon={<CloseOutlined />}
        />
      </div>
      <Transfer
        style={{
          flex: 1
        }}
        listStyle={{
          width: '100%',
          height: '60vh',
          minHeight: '500px'
        }}
        showSearch={true}
        dataSource={data}
        titles={['Source', 'Target']}
        targetKeys={targetKeys}
        selectedKeys={selectedKeys}
        onChange={onChange}
        onSelectChange={onSelectChange}
        render={(item: RecordType) => item.title}
      />
      <Divider />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <Button
          loading={loading}
          onClick={() => {
            setLoading(true)
            handleDone().finally(() => setLoading(false))
          }}
          type={'primary'}
          icon={<SaveOutlined />}
        >
          Save
        </Button>
      </div>
    </Modal>
  )
}

export default DeviceSelectionModal
